import { ReactNode } from 'react'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import { getBigAssetPath, getSmallAssetPath } from '@aytm/lib/common/assets'
import { unescapeTags } from '@aytm/lib/common/func'
import {
  EcommerceBannerPlacement,
  EcommerceResponse,
  IEcommerceAnswerSchema,
  IEcommerceQuestionSchema
} from '../../types.ts'
import { formatPriceWithLocale } from '../../utils/formatPriceWithLocale.ts'
import { hasDiscount } from '../../utils/hasDiscount.ts'
import { useChildrenProducts } from '../../hooks/useChildrenProducts.tsx'
import AssetsGate from '../../components/AssetsGate.tsx'
import Spinner from './Spinner.tsx'
import AddToCartButton from './AddToCartButton.tsx'
import AldiPriceMatch from './AldiPriceMatch.tsx'
import Badges from './Badges.tsx'
import Banner from './Banner.tsx'
import ClubcardPrice from './ClubcardPrice.tsx'
import style from './Cart.module.scss'
import basketGreyIcon from '../assets/icons/basketGrey.svg'
import couponIcon from '../assets/icons/coupon.svg'
import rateStarIcon from '../assets/icons/rateStar.svg'

const RATE_LABELS = ['rateVeryDissatisfied', 'rateDissatisfied', 'rateNeutral', 'rateSatisfied', 'rateVerySatisfied']

interface ICartProps {
  schema: IEcommerceQuestionSchema
  response: EcommerceResponse
  total: number
  totalPrice: number
  onCartClose: () => void
  onItemSelect: (index: number, bannerPlacement?: EcommerceBannerPlacement) => void
  onAddToCart: (index: number, qnt: number, inCarousel?: boolean) => void
  onDeleteFromCart: (index: number) => void
  onUpdateResponse: (response: EcommerceResponse) => void
  checkout: () => void
}

interface ICartLineProps {
  schema: IEcommerceQuestionSchema
  answer: IEcommerceAnswerSchema
  amount: number
  // The price the response counts: the Clubcard price when set, like the summary and the spend limit.
  linePrice: number
  onSelect: () => void
  onAdd: (qnt: number) => void
  onDelete: () => void
}

// The checkboxes and links of the site are drawn but inert, like every other control the task does not use.
const InertCheck = ({ label }: { label: string }) => (
  <EmptyLink color="#00539f">
    <span className={style.check}>
      <span className={style.checkBox} />
      {label}
    </span>
  </EmptyLink>
)

const CartLine = ({ schema, answer, amount, linePrice, onSelect, onAdd, onDelete }: ICartLineProps) => {
  const { t, languageTag } = useSurveyLocale()
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  const hasVariants = useChildrenProducts({ answer, schema, excludeCurrentProduct: true }).length > 0

  return (
    <div className={style.line} data-testid="SW-EC-CartItem">
      <div className={style.lineMain}>
        <div className={style.lineThumb} onClick={onSelect}>
          {answer.image ? (
            <span
              className={style.lineImage}
              role="img"
              aria-label={answer.image.alt_text || t('questions', 'img', 'noAlt')}
              style={{ backgroundImage: `url('${getSmallAssetPath(answer.image)}')` }}
            />
          ) : (
            <span className={style.lineNoImage} />
          )}
        </div>

        <div className={style.lineInfo}>
          <div className={style.lineTitle} onClick={onSelect} dangerouslySetInnerHTML={{ __html: answer.text }} />
          {hasVariants && (
            <span className={style.moreLikeThis} onClick={onSelect}>
              {t('questions', 'ec', 'tesco', 'moreLikeThis')}
              <i className={`pi pi-angle-right ${style.moreLikeThisChevron}`} />
            </span>
          )}
          <ClubcardPrice schema={schema} answer={answer} />
          <AldiPriceMatch answer={answer} />
        </div>

        <div className={style.lineBuy}>
          <div className={style.linePrices}>
            <span className={style.linePrice} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
            {answer.per_count_price && (
              <span
                className={style.lineUnitPrice}
                dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }}
              />
            )}
          </div>
          <AddToCartButton amount={amount} limit={schema.max_qty_per_product} cartLine onAdd={onAdd} />
          <div className={style.lineTotal}>
            {t('questions', 'ec', 'tesco', 'lineTotal')}{' '}
            <b dangerouslySetInnerHTML={{ __html: price(amount * linePrice) }} />
          </div>
        </div>

        <button
          type="button"
          className={style.remove}
          onClick={onDelete}
          aria-label={t('questions', 'ec', 'tesco', 'remove')}
        >
          <i className="pi pi-times" />
        </button>
      </div>

      <div className={style.lineActions}>
        <InertCheck label={t('questions', 'ec', 'tesco', 'addNote')} />
        <span className={style.divider} />
        <InertCheck label={t('questions', 'ec', 'tesco', 'allowSubstitutions')} />
        <span className={style.divider} />
        <EmptyLink color="#00539f">
          <span className={style.saveForLater}>{t('questions', 'ec', 'tesco', 'saveForLater')}</span>
        </EmptyLink>
      </div>
    </div>
  )
}

// The app's line: labels, photo beside the title and the buy line, then the inert Sub / Note and the
// basket count; the offers run the full width underneath. No remove cross, line total or Save for later.
const CartLineMobile = ({
  schema,
  answer,
  amount,
  onSelect,
  onAdd
}: Omit<ICartLineProps, 'linePrice' | 'onDelete'>) => {
  const { t, languageTag } = useSurveyLocale()
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)

  return (
    <div className={`${style.line} ${style.lineMobile}`} data-testid="SW-EC-CartItem">
      <Badges answer={answer} />
      <div className={style.lineMain}>
        <div className={style.lineThumb} onClick={onSelect}>
          {answer.image ? (
            <span
              className={style.lineImage}
              role="img"
              aria-label={answer.image.alt_text || t('questions', 'img', 'noAlt')}
              style={{ backgroundImage: `url('${getSmallAssetPath(answer.image)}')` }}
            />
          ) : (
            <span className={style.lineNoImage} />
          )}
        </div>
        <div className={style.lineInfo}>
          <div className={style.lineTitle} onClick={onSelect} dangerouslySetInnerHTML={{ __html: answer.text }} />
          <div className={style.lineBuy}>
            <div className={style.linePrices}>
              <span className={style.linePrice} dangerouslySetInnerHTML={{ __html: price(answer.price) }} />
              {answer.per_count_price && (
                <span
                  className={style.lineUnitPrice}
                  dangerouslySetInnerHTML={{ __html: unescapeTags(answer.per_count_price) }}
                />
              )}
            </div>
            <AddToCartButton amount={amount} limit={schema.max_qty_per_product} cartLine onAdd={onAdd} />
          </div>
        </div>
      </div>
      <div className={style.lineActions}>
        <EmptyLink color="#00539f">
          <span className={style.lineCheck}>
            <span className={style.lineCheckLabel}>{t('questions', 'ec', 'tesco', 'substitute')}</span>
            <span className={style.checkBox} />
          </span>
        </EmptyLink>
        <EmptyLink color="#00539f">
          <span className={style.lineCheck}>
            <span className={style.lineCheckLabel}>{t('questions', 'ec', 'tesco', 'note')}</span>
            <span className={style.checkBox} />
          </span>
        </EmptyLink>
        <span className={style.inBasket}>
          <i className="pi pi-check" />
          {t<(count: number) => string>('questions', 'ec', 'tesco', 'inBasket')(amount)}
        </span>
      </div>
      <div className={style.lineOffers}>
        <ClubcardPrice schema={schema} answer={answer} />
        <AldiPriceMatch answer={answer} />
      </div>
    </div>
  )
}

const Cart = ({
  schema,
  response,
  total,
  totalPrice,
  onCartClose,
  onItemSelect,
  onAddToCart,
  onDeleteFromCart,
  onUpdateResponse,
  checkout
}: ICartProps) => {
  const { t, languageTag } = useSurveyLocale()
  const timeline = useTimeline()
  const viewport = useViewport()
  const price = (value: number) => formatPriceWithLocale(value, schema.price_mask, languageTag, true, false)
  const na = schema.answers.find(answer => answer.na)

  const items = schema.answers.filter(answer => !answer.na && response.answers[answer.index].v > 0)
  // The band needs both its creative and a product to promote.
  const bannerAnswer = schema.ad_image2
    ? schema.answers.find(answer => answer.banner && !answer.na && !answer.parent_product)
    : undefined
  // The banner and the lines wait for their images together; the summary is always there. Both gates
  // take the same URLs, so they open on the same render.
  const cartImages = [
    bannerAnswer && getBigAssetPath(schema.ad_image2),
    ...items.map(answer => answer.image && getSmallAssetPath(answer.image))
  ]

  // Cart caps are advisory while shopping and only bite at checkout — the shared behaviour of every retailer.
  const valid =
    total > 0 &&
    (schema.max_money_spent ? totalPrice <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? total <= schema.max_cart_size : true)
  const cartError = [
    schema.max_money_spent && totalPrice > schema.max_money_spent
      ? t<(max: string) => string>('questions', 'ec', 'maxSpentError')(price(schema.max_money_spent))
      : '',
    schema.max_cart_size && total > schema.max_cart_size
      ? t<(max: number) => string>('questions', 'ec', 'maxCartCountError')(schema.max_cart_size)
      : ''
  ]
    .filter(Boolean)
    .join('')

  // "None of these" zeroes every other answer; the view's na effect then finishes the question.
  const onNaSelect = () => {
    if (!na) return
    timeline.log({ type: 'na_select', ref: na.id })
    response.answers = response.answers.map(answer => ({ ...answer, v: answer.id === na.id ? 1 : 0 }))
    onUpdateResponse(response)
  }

  const naButton = na && (
    <button type="button" className={style.pillButton} onClick={onNaSelect} data-testid="SW-EC-Cart-None">
      <span dangerouslySetInnerHTML={{ __html: na.text }} />
    </button>
  )

  const emptyBlock = (title: string, hint: string, actions?: ReactNode) => (
    <div className={style.empty}>
      <span className={style.emptyBadge}>
        <img src={basketGreyIcon} alt="" role="presentation" className={style.emptyIcon} />
      </span>
      <div className={style.emptyTitle}>{title}</div>
      <div className={style.emptyHint}>{hint}</div>
      {actions && <div className={style.emptyActions}>{actions}</div>}
    </div>
  )

  const emptyBasket = emptyBlock(
    t('questions', 'ec', 'tesco', 'yourBasketEmpty'),
    t('questions', 'ec', 'tesco', 'yourBasketEmptyHint'),
    <>
      <EmptyLink color="#00539f">
        <span className={style.pillButton}>{t('questions', 'ec', 'tesco', 'shopFavourites')}</span>
      </EmptyLink>
      <button type="button" className={style.pillButton} onClick={onCartClose}>
        {t('questions', 'ec', 'tesco', 'browseStore')}
      </button>
      {naButton}
    </>
  )

  const lines = (renderLine: (answer: IEcommerceAnswerSchema) => ReactNode) => (
    <AssetsGate urls={cartImages} loader={<Spinner />}>
      <div className={style.lines}>{items.map(renderLine)}</div>
    </AssetsGate>
  )

  const checkoutButton = (
    <button
      type="button"
      className={style.checkout}
      onClick={checkout}
      disabled={!valid}
      data-testid="SW-EC-Cart-Checkout"
    >
      {t('questions', 'ec', 'tesco', 'checkout')}
    </button>
  )

  // A filled basket offers no "None of these": the option lives on the empty basket only, as in every store.
  const emptyBasketRow = (
    <div className={style.emptyBasketRow}>
      <EmptyLink color="#00539f">
        <span className={style.pillButton}>{t('questions', 'ec', 'tesco', 'emptyBasket')}</span>
      </EmptyLink>
    </div>
  )

  if (viewport.mobile) {
    // The app's empty basket is the empty block alone; there is nothing to sum or check out.
    if (0 === total) {
      return (
        <div className={`${style.cart} ${style.cartMobile} ${style.cartMobileEmpty}`} data-testid="SW-EC-Cart">
          {emptyBasket}
        </div>
      )
    }

    // What the Clubcard prices take off the shelf prices of everything in the basket.
    const offerSavings = items.reduce(
      (sum, answer) =>
        hasDiscount(answer) ? sum + (answer.price - answer.discounted_price) * response.answers[answer.index].v : sum,
      0
    )

    return (
      <div className={`${style.cart} ${style.cartMobile}`} data-testid="SW-EC-Cart">
        <div className={style.mobileSummary} data-testid="SW-EC-Cart-Summary">
          <div className={style.mobileSummaryCell}>
            <span className={style.mobileSummaryLabel}>{t('questions', 'ec', 'tesco', 'offerSavings')}</span>
            <span className={style.mobileSummaryValue} dangerouslySetInnerHTML={{ __html: price(offerSavings) }} />
          </div>
          <div className={style.mobileSummaryCell}>
            <span className={style.mobileSummaryLabel}>{t('questions', 'ec', 'tesco', 'guidePrice')}</span>
            <span className={style.mobileSummaryValue} dangerouslySetInnerHTML={{ __html: price(totalPrice) }} />
          </div>
        </div>

        <div className={style.mobileBlocks}>
          <div className={style.delivery}>
            <div className={style.deliveryTitle}>{t('questions', 'ec', 'tesco', 'deliveryTitle')}</div>
            <div className={style.deliveryHint}>{t('questions', 'ec', 'tesco', 'deliveryHint')}</div>
            <EmptyLink color="#00539f">
              <span className={`${style.pillButton} ${style.deliveryButton}`}>
                {t('questions', 'ec', 'tesco', 'bookSlot')}
              </span>
            </EmptyLink>
          </div>

          {bannerAnswer && (
            <AssetsGate urls={cartImages}>
              <Banner schema={schema} answer={bannerAnswer} onItemSelect={onItemSelect} />
            </AssetsGate>
          )}
        </div>

        <div className={style.itemsBar}>
          <span className={style.itemsCount}>
            {schema.max_cart_size
              ? t<(count: number, max: number) => string>(
                  'questions',
                  'ec',
                  'tesco',
                  'itemsCountMaximum'
                )(total, schema.max_cart_size)
              : t<(count: number) => string>('questions', 'ec', 'tesco', 'itemsCount')(total)}
          </span>
          <EmptyLink color="#00539f">
            <span className={`${style.pillButton} ${style.sortButton}`}>{t('questions', 'ec', 'tesco', 'sort')}</span>
          </EmptyLink>
        </div>
        <div className={style.groupTitle}>{t('questions', 'ec', 'tesco', 'groceries')}</div>
        {lines(answer => (
          <CartLineMobile
            key={answer.id}
            schema={schema}
            answer={answer}
            amount={response.answers[answer.index].v}
            onSelect={() => onItemSelect(answer.index)}
            onAdd={qnt => onAddToCart(answer.index, qnt)}
          />
        ))}
        {emptyBasketRow}

        <div className={style.checkoutBar}>
          {cartError && <div className={style.error} dangerouslySetInnerHTML={{ __html: cartError }} />}
          {checkoutButton}
        </div>
      </div>
    )
  }

  return (
    <div className={style.cart} data-testid="SW-EC-Cart">
      <div className={style.left}>
        <h1 className={style.title}>{t('questions', 'ec', 'tesco', 'basket')}</h1>

        {bannerAnswer && (
          <AssetsGate urls={cartImages}>
            <Banner schema={schema} answer={bannerAnswer} onItemSelect={onItemSelect} />
          </AssetsGate>
        )}

        <div className={style.searchLabel}>{t('questions', 'ec', 'tesco', 'searchBasket')}</div>
        <EmptyLink color="#00539f">
          <div className={style.search}>
            <i className={`pi pi-search ${style.searchIcon}`} />
            <input
              type="text"
              className={style.searchInput}
              placeholder={t('questions', 'ec', 'tesco', 'enterAnItem')}
              readOnly
              aria-label={t('questions', 'ec', 'tesco', 'searchBasket')}
            />
          </div>
        </EmptyLink>

        {0 === total ? (
          emptyBasket
        ) : (
          <>
            <h2 className={style.sectionTitle}>{t('questions', 'ec', 'tesco', 'groceries')}</h2>
            <div className={style.slot}>
              <span className={style.slotText}>{t('questions', 'ec', 'tesco', 'reserveSlot')}</span>
              <EmptyLink color="#00539f">
                <span className={style.textLink}>{t('questions', 'ec', 'tesco', 'bookSlot')}</span>
              </EmptyLink>
            </div>
            <div className={style.substitutions}>
              <InertCheck label={t('questions', 'ec', 'tesco', 'allowSubstitutions')} />
              <EmptyLink color="#00539f">
                <span className={style.textLink}>{t('questions', 'ec', 'tesco', 'moreInfo')}</span>
              </EmptyLink>
            </div>
            {lines(answer => (
              <CartLine
                key={answer.id}
                schema={schema}
                answer={answer}
                amount={response.answers[answer.index].v}
                linePrice={response.answers[answer.index].price}
                onSelect={() => onItemSelect(answer.index)}
                onAdd={qnt => onAddToCart(answer.index, qnt)}
                onDelete={() => onDeleteFromCart(answer.index)}
              />
            ))}
            {emptyBasketRow}
          </>
        )}

        <h2 className={style.sectionTitle}>{t('questions', 'ec', 'tesco', 'savedForLater')}</h2>
        {emptyBlock(t('questions', 'ec', 'tesco', 'savedEmpty'), t('questions', 'ec', 'tesco', 'savedEmptyHint'))}

        <div className={style.rate}>
          <div className={style.rateTitle}>{t('questions', 'ec', 'tesco', 'rateBasket')}</div>
          <EmptyLink color="#00539f">
            <span className={style.rateStars}>
              {RATE_LABELS.map(key => (
                <img key={key} src={rateStarIcon} alt={t('questions', 'ec', 'tesco', key)} className={style.rateStar} />
              ))}
            </span>
          </EmptyLink>
        </div>
      </div>

      <aside className={style.right}>
        <section className={`${style.card} ${style.coupons}`}>
          <span className={style.couponsTitle}>
            {t<(count: number) => string>('questions', 'ec', 'tesco', 'coupons')(0)}
          </span>
          <EmptyLink color="#00539f">
            <span className={style.viewAll}>
              <img src={couponIcon} alt="" role="presentation" className={style.couponIcon} />
              {t('questions', 'ec', 'tesco', 'viewAll')}
            </span>
          </EmptyLink>
        </section>

        <section className={style.card} data-testid="SW-EC-Cart-Summary">
          <h2 className={style.summaryTitle}>{t('questions', 'ec', 'tesco', 'basketSummary')}</h2>
          <div className={style.summaryGroup}>
            <span>{t('questions', 'ec', 'tesco', 'groceries')}</span>
            <i className="pi pi-chevron-circle-up" />
          </div>
          <div className={`${style.summaryRow} ${style.summaryItems}`}>
            <span>
              {schema.max_cart_size
                ? t<(max: number) => string>('questions', 'ec', 'tesco', 'itemsMaximum')(schema.max_cart_size)
                : t('questions', 'ec', 'tesco', 'items')}
            </span>
            <span>{total}</span>
          </div>
          <div className={style.summaryRow}>
            <span>{t('questions', 'ec', 'tesco', 'guidePrice')}</span>
            <span className={style.money} dangerouslySetInnerHTML={{ __html: price(totalPrice) }} />
          </div>
          <EmptyLink color="#00539f">
            <span className={style.textLink}>{t('questions', 'ec', 'tesco', 'whatsGuidePrice')}</span>
          </EmptyLink>
          <div className={`${style.summaryRow} ${style.summaryTotal}`}>
            <span>{t('questions', 'ec', 'tesco', 'totalEstimated')}</span>
            <span className={style.money} dangerouslySetInnerHTML={{ __html: price(totalPrice) }} />
          </div>
          {cartError && <div className={style.error} dangerouslySetInnerHTML={{ __html: cartError }} />}
          {checkoutButton}
        </section>
      </aside>
    </div>
  )
}

export default Cart
